import * as AntIcons from "@ant-design/icons";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { type IconBaseProps } from "@ant-design/icons/lib/components/Icon";
import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Switch,
  Tag,
  Typography,
  type InputRef,
} from "antd";
import { type MessageInstance } from "antd/lib/message/interface";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import AiAgentApi from "../../api";
import { fullWidth } from "../../styles";
import {
  AIAgentCompanyWithPromptBankAccess,
  AiAgentRecommendedPrompt,
  AiAgentRecommendedPromptCategory,
  AiAgentRecommendedPromptPayload,
} from "../../types";

const { Title, Text } = Typography;
const { Item } = Form;
const { TextArea } = Input;

interface UpsertModalProps {
  open: boolean;
  data: AiAgentRecommendedPromptPayload | null;
}

export type AntIconName = keyof typeof AntIcons;
type AntIconComponent = ComponentType<IconBaseProps>;

interface DynamicIconProps extends IconBaseProps {
  iconName: AntIconName;
}

export function DynamicIcon({ iconName, ...props }: DynamicIconProps) {
  const IconComponent = AntIcons[iconName] as AntIconComponent;
  return <IconComponent {...props} />;
}

export default function AiAgentAdminPromptBank({ token }: { token: string }) {
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [messageApi, messageContextHolder] = message.useMessage();

  const [upsertModalProps, setUpsertModalProps] = useState<UpsertModalProps>({ open: false, data: null });

  const [companiesWithPromptBankAccess, setCompaniesWithPromptBankAccess] = useState<
    AIAgentCompanyWithPromptBankAccess[]
  >([]);

  const [recommendedPrompts, setRecommendedPrompts] = useState<AiAgentRecommendedPromptPayload[]>([]);
  const [categories, setCategories] = useState<AiAgentRecommendedPromptCategory[]>([]);

  const [filter, setFilter] = useState({
    search: "",
    category: 0,
  });

  const filteredRecommendedPrompts = useMemo(() => {
    const { search, category } = filter;
    if (!search && !category) return recommendedPrompts;
    return recommendedPrompts.filter(({ title, categoryId, content }) => {
      const matchesSearch =
        !search ||
        title.toLowerCase().includes(search.toLowerCase()) ||
        content.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = !category || category === categoryId;
      return matchesSearch && matchesCategory;
    });
  }, [filter, recommendedPrompts]);

  useEffect(() => {
    getRecommendedPrompts();
    getRecommendedPromptsCategories();
    getCompaniesWithPromptBankAccess();
  }, []);

  function getRecommendedPrompts() {
    new AiAgentApi(token).getRecommendedPrompts().then(setRecommendedPrompts);
  }

  function getRecommendedPromptsCategories() {
    new AiAgentApi(token).getRecommendedPromptsCategories().then(setCategories);
  }

  async function getCompaniesWithPromptBankAccess() {
    await axios
      .get("/ai-agent/companies/prompt-bank-access")
      .then(({ data }) => {
        setCompaniesWithPromptBankAccess(data);
      })
      .catch((_) => {});
  }

  async function deleteRecommendedPrompt(id: string) {
    const result = await new AiAgentApi(token).deleteRecommendedPrompt(id);
    if (result) {
      messageApi.success("O prompt foi deletado com sucesso.");
      setRecommendedPrompts((prev) => prev.filter((e) => e.id !== id));
    } else {
      messageApi.error("Erro ao tentar deletar o prompt, tente novamente.");
    }
  }

  return (
    <>
      {modalContextHolder}
      {messageContextHolder}
      <UpsertModal
        props={upsertModalProps}
        cancel={() => {
          setUpsertModalProps({ open: false, data: null });
        }}
        categories={categories}
        setCategories={setCategories}
        setRecommendedPrompts={setRecommendedPrompts}
        token={token}
        messageApi={messageApi}
      />
      <Flex
        style={{ ...fullWidth }}
        vertical
        gap={16}
      >
        <Row>
          <Col span={18}>
            <Title
              style={{ color: "#884EA6", margin: 0 }}
              level={4}
            >
              Gerencie templates de prompts para uso no agente de I.A
            </Title>
          </Col>
          <Col
            span={6}
            style={{ textAlign: "right" }}
          >
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => {
                setUpsertModalProps({ open: true, data: null });
              }}
            >
              Novo template
            </Button>
          </Col>
        </Row>

        <Row gutter={8}>
          <Col span={20}>
            <Input
              placeholder="Busque por um template..."
              suffix={<SearchOutlined />}
              value={filter.search}
              onChange={({ target }) => {
                setFilter((prev) => ({ ...prev, search: target.value }));
              }}
            />
          </Col>
          <Col span={4}>
            <Select
              style={{ ...fullWidth }}
              placeholder="Categoria"
              value={filter.category ? filter.category : null}
              onChange={(value) => {
                setFilter((prev) => ({ ...prev, category: value || 0 }));
              }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option ? option.label.toLowerCase().includes(input.toLowerCase()) : false
              }
              options={categories.map(({ id, description }) => ({ value: id, label: description }))}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <CustomCard
              title="Total de templates"
              value={filteredRecommendedPrompts.length}
            />
          </Col>
          <Col span={6}>
            <CustomCard
              title="Categorias"
              value={categories.length}
            />
          </Col>
          <Col span={6}>
            <CustomCard
              title="Ativos"
              value={filteredRecommendedPrompts.filter(({ active }) => active).length}
            />
          </Col>
          <Col span={6}>
            <CustomCard
              title="Empresas com acesso"
              value={companiesWithPromptBankAccess.length}
            >
              <Button
                size="small"
                icon={<EyeOutlined style={{ color: "#884EA6" }} />}
                type="text"
                onClick={() =>
                  modalApi.info({
                    closable: true,
                    footer: false,
                    icon: null,
                    title: "Empresas com acesso ao banco de prompts",
                    content: (
                      <List
                        dataSource={companiesWithPromptBankAccess}
                        renderItem={(item) => (
                          <List.Item>
                            <List.Item.Meta
                              title={item.name}
                              description={item.email}
                            />
                          </List.Item>
                        )}
                        pagination={
                          companiesWithPromptBankAccess.length
                            ? {
                                simple: true,
                                align: "center",
                                pageSize: 5,
                                showSizeChanger: false,
                              }
                            : false
                        }
                      />
                    ),
                  })
                }
              />
            </CustomCard>
          </Col>
        </Row>

        <List
          dataSource={filteredRecommendedPrompts}
          renderItem={(item) => (
            <Card
              style={{ marginBottom: 16 }}
              title={
                <Flex justify="space-between">
                  <Title
                    style={{ color: "#884EA6", margin: 0 }}
                    level={5}
                  >
                    {item.title}
                  </Title>
                  <Flex gap={4}>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard
                          .writeText(item.content)
                          .then((_) => messageApi.success("Prompt copiado para a área de transferência com sucesso."))
                          .catch((_) =>
                            messageApi.warning("Não foi possível copiar o prompt para a área de transferência."),
                          );
                      }}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setUpsertModalProps({ open: true, data: item });
                      }}
                    />
                    <Popconfirm
                      placement="topLeft"
                      title="Tem certeza que deseja deletar este prompt?"
                      cancelText="Não"
                      okText="Sim"
                      okButtonProps={{ danger: true, ghost: true }}
                      arrow={false}
                      onConfirm={async () => {
                        await deleteRecommendedPrompt(item.id);
                      }}
                    >
                      <Button
                        danger
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  </Flex>
                </Flex>
              }
            >
              <Flex
                vertical
                gap={8}
              >
                <Text
                  style={{ fontSize: "small" }}
                  type="secondary"
                >
                  Criado em: {dayjs(item.createdAt).format("DD/MM/YYYY [às] HH:mm")}
                </Text>
                <Flex>
                  <Tag color={item.active ? "green" : "gold"}>Status: {item.active ? "Ativo" : "Inativo"}</Tag>
                  <Tag color="blue">Categoria: {categories.find(({ id }) => item.categoryId === id)?.description}</Tag>
                  {item.icon && (
                    <Tag
                      style={{ marginInlineEnd: 0 }}
                      color="purple"
                    >
                      <DynamicIcon
                        iconName={item.icon as AntIconName}
                        style={{ color: "dodgerblue" }}
                      />
                    </Tag>
                  )}
                </Flex>
                <Tag style={{ marginInlineEnd: 0, marginTop: 4, padding: 8, whiteSpace: "normal" }}>{item.content}</Tag>
              </Flex>
            </Card>
          )}
          pagination={
            filteredRecommendedPrompts.length
              ? {
                  align: "center",
                  pageSize: 5,
                }
              : false
          }
        />
      </Flex>
    </>
  );
}

function CustomCard({ title, value, children }: { title: string; value: number; children?: React.ReactNode }) {
  return (
    <Card
      style={{ boxShadow: "0 1px 3px #837EFF4D" }}
      styles={{ body: { padding: 12 } }}
    >
      <Flex vertical>
        <Flex
          style={{ height: 24 }}
          justify="space-between"
          gap={4}
        >
          <Text
            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            type="secondary"
          >
            {title}
          </Text>
          {children}
        </Flex>
        <Text style={{ color: "#884EA6" }}>{value}</Text>
      </Flex>
    </Card>
  );
}

const rawRecommendedPrompt = {
  title: "",
  categoryId: 0,
  content: "",
  active: true,
  icon: null,
};

function UpsertModal({
  props,
  cancel,
  categories,
  setCategories,
  setRecommendedPrompts,
  token,
  messageApi,
}: {
  props: UpsertModalProps;
  cancel: () => void;
  categories: AiAgentRecommendedPromptCategory[];
  setCategories: React.Dispatch<React.SetStateAction<AiAgentRecommendedPromptCategory[]>>;
  setRecommendedPrompts: React.Dispatch<React.SetStateAction<AiAgentRecommendedPromptPayload[]>>;
  token: string;
  messageApi: MessageInstance;
}) {
  const { open, data } = props;

  const inputRef = useRef<InputRef>(null);

  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [creatingRecommendedPrompt, setCreatingRecommendedPrompt] = useState(false);
  const [recommendedPrompt, setRecommendedPrompt] = useState<AiAgentRecommendedPrompt>(rawRecommendedPrompt);

  const [selectedIcon, setSelectedIcon] = useState<AntIconName>("CloudOutlined");

  const iconOptions: AntIconName[] = Object.keys(AntIcons) as AntIconName[];

  useEffect(() => {
    if (data) {
      setRecommendedPrompt({
        title: data.title,
        categoryId: data.categoryId,
        content: data.content,
        active: data.active,
        icon: data.icon,
      });
    }
  }, [open]);

  async function createCategory(_: React.MouseEvent<HTMLButtonElement>) {
    setCreatingCategory(true);
    const newCategory = await new AiAgentApi(token).createRecommendedPromptsCategory(newCategoryDescription.trim());
    if (newCategory) {
      setCategories((prev) => [newCategory, ...prev]);
    } else {
      messageApi.error("A categoria que você tentou cadastrar já existe.");
    }
    setNewCategoryDescription("");
    inputRef.current?.focus();
    setCreatingCategory(false);
  }

  async function createRecommendedPrompt() {
    setCreatingRecommendedPrompt(true);
    const { title, categoryId, content, active, icon } = recommendedPrompt;
    const form = {
      title: title.trim(),
      categoryId,
      content: content.trim(),
      active,
      icon: icon ?? null,
    };
    const aiAgentApi = new AiAgentApi(token);
    const newRecommendedPrompt = !data
      ? await aiAgentApi.createRecommendedPrompt(form)
      : await aiAgentApi.updateRecommendedPrompt(data.id, form);
    if (newRecommendedPrompt) {
      messageApi.success(`Prompt ${!data ? "cadastrado" : "atualizado"} com sucesso.`);
      if (!data) {
        setRecommendedPrompts((prev) => [newRecommendedPrompt, ...prev]);
        handleCancel();
      } else {
        setRecommendedPrompts((prev) => prev.map((e) => (e.id === newRecommendedPrompt.id ? newRecommendedPrompt : e)));
      }
    } else {
      messageApi.error(`Erro ao tentar ${!data ? "cadastrar" : "atualizar"} o prompt, tente novamente.`);
    }
    setCreatingRecommendedPrompt(false);
  }

  function handleCancel() {
    cancel();
    setNewCategoryDescription("");
    setRecommendedPrompt(rawRecommendedPrompt);
  }

  return (
    <Modal
      width={450}
      open={open}
      onCancel={handleCancel}
      title={!data ? "Criar novo template" : "Editar template"}
      cancelButtonProps={{ style: { display: "none" } }}
      okText={!data ? "Criar template" : "Salvar alterações"}
      okButtonProps={{
        disabled: !recommendedPrompt.title || !recommendedPrompt.categoryId || !recommendedPrompt.content,
        loading: creatingRecommendedPrompt,
      }}
      onOk={createRecommendedPrompt}
    >
      <Flex
        vertical
        gap={8}
      >
        <Text type="secondary">
          {!data
            ? "Preencha os campos abaixo para criar um novo template de prompt."
            : "As alterações feitas surtirão efeito para todos os usuários com acesso ao banco de prompts."}
        </Text>
        <Form layout="vertical">
          <Item
            label="Título"
            required
          >
            <Input
              maxLength={100}
              value={recommendedPrompt.title}
              onChange={({ target }) => {
                setRecommendedPrompt((prev: any) => ({ ...prev, title: target.value }));
              }}
            />
          </Item>
          <Item
            label="Categoria"
            required
          >
            <Select
              value={recommendedPrompt.categoryId ? recommendedPrompt.categoryId : null}
              onChange={(value) => {
                setRecommendedPrompt((prev: any) => ({ ...prev, categoryId: value }));
              }}
              showSearch
              filterOption={(input, option) =>
                option ? option.label.toLowerCase().includes(input.toLowerCase()) : false
              }
              options={categories.map(({ id, description }) => ({ value: id, label: description }))}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Flex
                    justify="space-between"
                    gap={8}
                    style={{ padding: "0 4px 4px" }}
                  >
                    <Input
                      maxLength={100}
                      placeholder="Descrição da categoria..."
                      ref={inputRef}
                      value={newCategoryDescription}
                      onChange={({ target }) => {
                        setNewCategoryDescription(target.value);
                      }}
                    />
                    <Button
                      style={{ color: !newCategoryDescription ? undefined : "#884EA6" }}
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={createCategory}
                      disabled={!newCategoryDescription}
                      loading={creatingCategory}
                    >
                      Adicionar categoria
                    </Button>
                  </Flex>
                </>
              )}
            />
          </Item>
          <Item
            label="Prompt"
            required
          >
            <TextArea
              maxLength={250}
              autoSize={{ minRows: 2, maxRows: 4 }}
              value={recommendedPrompt.content}
              onChange={({ target }) => {
                setRecommendedPrompt((prev: any) => ({ ...prev, content: target.value }));
              }}
            />
          </Item>
          <Item label="Ícone">
            <Select
              allowClear
              showSearch
              value={recommendedPrompt.icon as AntIconName}
              onChange={(value: AntIconName) => {
                setRecommendedPrompt((prev: any) => ({ ...prev, icon: value }));
              }}
              optionLabelProp="label"
            >
              {iconOptions.map((icon) => (
                <Select.Option
                  key={icon}
                  value={icon}
                  label={icon}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <DynamicIcon
                      iconName={icon}
                      style={{ fontSize: 20 }}
                    />
                    <span>{icon}</span>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Item>
          <Item
            style={{ marginBottom: 0 }}
            label="Status"
          >
            <Switch
              checked={recommendedPrompt.active}
              onChange={(value) => {
                setRecommendedPrompt((prev: any) => ({ ...prev, active: value }));
              }}
            />
          </Item>
        </Form>
      </Flex>
    </Modal>
  );
}
